import { call, select } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { SignalingSocket } from 'services/signaling';
import {
  embedPlayerUserAction,
  selectActiveFile,
  selectContentLibraryMediaPlayer,
} from 'features/content-library/contentLibrarySlice';
import {
  ContentLibraryMediaPlayer,
  ContentLibraryMediaPlayerRemotePayload,
  ContentLibraryOpenedFile,
} from 'features/content-library/types';
import { logger } from 'utils/logger';

// Bug B paired-arrival watch: a broadcast within 100 ms of the last inbound payload
// is the receiver-echo signature. 3+ such events in a 5-min window is the escalation threshold.
const PAIRED_ARRIVAL_THRESHOLD_MS = 100;
const PAIRED_ARRIVAL_WINDOW_MS = 5 * 60 * 1000;
const PAIRED_ARRIVAL_ALERT_COUNT = 3;
type PairedArrivalState = { lastInboundAt: number; recentEchoes: number[] };
const pairedArrivalState: Map<string, PairedArrivalState> = new Map();

export const recordInboundForPairedArrivalWatch = (id: string) => {
  const entry = pairedArrivalState.get(id) ?? { lastInboundAt: 0, recentEchoes: [] };
  entry.lastInboundAt = performance.now();
  pairedArrivalState.set(id, entry);
};

export function* embedPlayerUserActionSaga(action: ReturnType<typeof embedPlayerUserAction>) {
  const mediaPlayer: ContentLibraryMediaPlayer | undefined = yield select(
    selectContentLibraryMediaPlayer,
    action.payload.id
  );
  if (!mediaPlayer) {
    return;
  }

  const file: ContentLibraryOpenedFile | undefined = yield select(
    selectActiveFile,
    action.payload.id
  );
  const isAudio = file?.type === 'audio';

  // Browsers fire `pause` after the natural `ended` event. This is the post-end pause —
  // don't broadcast it, as it would overwrite the 'ended' on the server for late joiners.
  if (mediaPlayer.state === 'ended' && action.payload.state === 'paused') {
    if (isAudio) {
      logger
        .remote({ capture: 'mp3-debug', nonProd: true })
        .log('[userActionSaga] not broadcasting (post-end pause)', {
          id: mediaPlayer.id,
        });
    }
    return;
  }

  // 'idle' is never sent on the wire — guard for type safety.
  if (mediaPlayer.state === 'idle') {
    return;
  }

  // state is always included so the server snapshot (pure relay) carries playback
  // state for late joiners — omitting it would leave joiners in 'paused'.
  // volume is only sent on type:'volume' and muted only on type:'mute' (Bug C:
  // Chrome's fresh-element volume:0 transient must not leak via other event types).
  const data: Partial<ContentLibraryMediaPlayerRemotePayload> = {
    id: mediaPlayer.id,
    currentTime: mediaPlayer.currentTime,
    state: mediaPlayer.state,
    type: action.payload.type,
  };
  if (action.payload.type === 'volume') {
    data.volume = mediaPlayer.volume;
  } else if (action.payload.type === 'mute') {
    data.muted = mediaPlayer.muted;
  }

  const entry = pairedArrivalState.get(mediaPlayer.id);
  const msSinceLastInbound =
    entry && entry.lastInboundAt > 0 ? performance.now() - entry.lastInboundAt : null;
  if (entry && msSinceLastInbound !== null && msSinceLastInbound < PAIRED_ARRIVAL_THRESHOLD_MS) {
    const now = performance.now();
    entry.recentEchoes = entry.recentEchoes.filter((t) => now - t < PAIRED_ARRIVAL_WINDOW_MS);
    entry.recentEchoes.push(now);
    if (entry.recentEchoes.length >= PAIRED_ARRIVAL_ALERT_COUNT) {
      const extra = {
        id: mediaPlayer.id,
        msSinceLastInbound: Math.round(msSinceLastInbound),
        echoCount: entry.recentEchoes.length,
      };
      Sentry.captureMessage('mp3-paired-arrival-echo', { level: 'warning', extra });
      if (isAudio) {
        logger
          .remote({ capture: 'mp3-debug', nonProd: true })
          .warn('[userActionSaga] paired-arrival echo', extra);
      }
      entry.recentEchoes = [];
    }
  }

  if (isAudio) {
    logger.remote({ capture: 'mp3-debug', nonProd: true }).log('[userActionSaga] broadcasting', {
      id: mediaPlayer.id,
      payloadType: action.payload.type,
      msSinceLastInbound: msSinceLastInbound === null ? null : Math.round(msSinceLastInbound),
      data,
    });
  }

  yield call(SignalingSocket.send, {
    event: 'changeMediaPlayerState',
    data,
  });
}
